import type { ReportRequest } from "../../domain/report/contracts";

type EdgeResult<T> = {
  data: T | null;
  error: { message: string } | null;
};

export type SupabaseClientLike = {
  functions: {
    invoke<T>(
      name: string,
      options?: { body?: unknown; method?: string },
    ): Promise<EdgeResult<T>>;
  };
};

export async function saveReportInput(
  client: SupabaseClientLike,
  request: ReportRequest,
): Promise<"saved" | "duplicate"> {
  const { data, error } = (await client.functions.invoke("save-intake", {
    body: { request },
  })) as EdgeResult<{ status?: "saved" | "duplicate" }>;
  if (error) {
    throw new Error(`save-intake thất bại: ${error.message}`);
  }
  if (!data?.status) {
    throw new Error("save-intake trả về phản hồi rỗng");
  }
  return data.status;
}

export async function listSavedInputs(
  client: SupabaseClientLike,
): Promise<Array<{ createdAt: string; request: ReportRequest }>> {
  const { data, error } = (await client.functions.invoke("list-intake", {
    method: "GET",
  })) as EdgeResult<{
    inputs?: Array<{ createdAt: string; request: ReportRequest }>;
    skipped?: number;
  }>;
  if (error) {
    throw new Error(`list-intake thất bại: ${error.message}`);
  }
  const inputs = data?.inputs ?? [];
  if ((data?.skipped ?? 0) > 0) {
    console.warn(
      `[history] ${data?.skipped} bản ghi không giải mã được và đã bỏ qua`,
    );
  }
  return inputs;
}

/**
 * Direct RLS-scoped access to report_narratives. Unlike intake PII (which
 * must flow through encrypting edge functions), a validated synthesis draft
 * is PII-free by contract, so the browser may read/write its own rows.
 */
export type NarrativeDraftRow = {
  input_hash: string;
  prompt_version: string;
  draft: unknown;
  updated_at: string;
};

export type NarrativeStoreClient = {
  from(table: string): {
    upsert(
      row: NarrativeDraftRow,
      options: { onConflict: string },
    ): PromiseLike<{ error: { message: string } | null }>;
    select(columns: string): {
      order(
        column: string,
        options: { ascending: boolean },
      ): PromiseLike<{
        data: NarrativeDraftRow[] | null;
        error: { message: string } | null;
      }>;
      eq(
        column: string,
        value: string,
      ): {
        maybeSingle(): PromiseLike<{
          data: NarrativeDraftRow | null;
          error: { message: string } | null;
        }>;
      };
    };
  };
};

/**
 * Single-row lookup used to hydrate a just-created report without pulling
 * every saved draft (that bulk path stays in listNarrativeDrafts for history).
 */
export async function getNarrativeDraft(
  client: NarrativeStoreClient,
  inputHash: string,
): Promise<unknown | null> {
  const { data, error } = await client
    .from("report_narratives")
    .select("draft")
    .eq("input_hash", inputHash)
    .maybeSingle();
  if (error) {
    throw new Error(`report_narratives lookup thất bại: ${error.message}`);
  }
  return data?.draft ?? null;
}

export async function saveNarrativeDraft(
  client: NarrativeStoreClient,
  inputHash: string,
  promptVersion: string,
  draft: unknown,
): Promise<void> {
  const { error } = await client.from("report_narratives").upsert(
    {
      input_hash: inputHash,
      prompt_version: promptVersion,
      draft,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,input_hash" },
  );
  if (error) {
    throw new Error(`report_narratives upsert thất bại: ${error.message}`);
  }
}

export async function listNarrativeDrafts(
  client: NarrativeStoreClient,
): Promise<Array<{ inputHash: string; draft: unknown }>> {
  const { data, error } = await client
    .from("report_narratives")
    .select("input_hash, draft")
    .order("updated_at", { ascending: false });
  if (error) {
    throw new Error(`report_narratives select thất bại: ${error.message}`);
  }
  return (data ?? []).map((row) => ({
    inputHash: row.input_hash,
    draft: row.draft,
  }));
}
